import { useState } from 'react'
import { payekoohFeatureScreens } from '../content/payekoohFeatureScreens'
import type { Locale, Product } from '../content/site'

/**
 * Feature posters supplied by Payekooh, including their own real Android phone
 * mockups. Never wrap them in a second synthetic device or crop their headlines.
 * The Cafe Bazaar CTA remains only in the parent product information.
 */
export function BazaarPhonePreview({
  product,
  locale,
  compact = false,
}: {
  product: Product
  locale: Locale
  compact?: boolean
}) {
  const [selected, setSelected] = useState(0)
  const slide = payekoohFeatureScreens[selected]
  const total = payekoohFeatureScreens.length

  function move(delta: number) {
    setSelected((previous) => (previous + delta + total) % total)
  }

  return (
    <div className={'case-art case-art--bazaar payekooh-showcase' + (compact ? ' case-art--compact' : '')}>
      <div className="payekooh-showcase__eyebrow" aria-hidden="true">
        <span className="payekooh-showcase__eyebrow-line" />
        PAYEKOOH / APP EXPERIENCE
      </div>

      <div className="payekooh-showcase__stage" role="group" aria-label={
        locale === 'fa'
          ? `گالری قابلیت‌های ${product.name.fa}`
          : `${product.name.en} feature gallery`
      }>
        <button
          className="payekooh-showcase__arrow payekooh-showcase__arrow--previous"
          type="button"
          onClick={() => move(-1)}
          aria-label={locale === 'fa' ? 'قابلیت قبلی' : 'Previous feature'}
        >
          <span aria-hidden="true">‹</span>
        </button>

        <figure className="payekooh-showcase__poster">
          <img
            key={slide.image}
            className="payekooh-showcase__poster-image"
            src={slide.image}
            alt={locale === 'fa'
              ? `تصویر معرفی قابلیت ${slide.title.fa} در پایه‌کوه`
              : `Payekooh promotional screenshot: ${slide.title.en}`}
            decoding="async"
            draggable={false}
            width="816"
            height={slide.id === 'stats' ? '1226' : '1450'}
          />
          <figcaption className="payekooh-showcase__caption" aria-live="polite" aria-atomic="true">
            <strong>{slide.title[locale]}</strong>
            <span>{slide.description[locale]}</span>
          </figcaption>
        </figure>

        <button
          className="payekooh-showcase__arrow payekooh-showcase__arrow--next"
          type="button"
          onClick={() => move(1)}
          aria-label={locale === 'fa' ? 'قابلیت بعدی' : 'Next feature'}
        >
          <span aria-hidden="true">›</span>
        </button>
      </div>

      <div className="payekooh-showcase__features" role="group" aria-label={
        locale === 'fa' ? 'انتخاب قابلیت اپلیکیشن پایه‌کوه' : 'Choose a Payekooh feature'
      }>
        {payekoohFeatureScreens.map((feature, index) => (
          <button
            key={feature.id}
            type="button"
            className={'payekooh-showcase__feature' + (index === selected ? ' is-active' : '')}
            aria-label={feature.title[locale]}
            aria-pressed={index === selected}
            onMouseEnter={() => setSelected(index)}
            onFocus={() => setSelected(index)}
            onClick={() => setSelected(index)}
          >
            <span className="payekooh-showcase__feature-image">
              <img src={feature.image} alt="" loading="lazy" decoding="async" />
            </span>
            <span className="payekooh-showcase__feature-name">{feature.title[locale]}</span>
          </button>
        ))}
      </div>

      <div className="payekooh-showcase__footnote">
        <span>{locale === 'fa' ? 'قابلیت‌های واقعی اپلیکیشن اندروید' : 'Actual Android app features'}</span>
        <span dir="ltr">{String(selected + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}</span>
      </div>
    </div>
  )
}
